import { useCallback, useMemo, useState } from 'react';

import { SurgeSchedule, SurgeTier } from '../api';
import { centsToDollars, dollarsToCents, formatCents } from '../format';

// The editable copy of a surge schedule behind the Rates tab and Manage surge
// pay (GRAVY-10443). Rates stay as the text the admin typed until they are
// saved, so "1." is not rewritten to "1.00" mid-keystroke.

export type DraftTier = { leadTimeMinutes: number; rate: string };
export type SurgeDraft = { enabled: boolean; tiers: DraftTier[] };

type Limits = { maxCents: number; leadTimeOptions: number[] };

// Rows are always held in firing order, longest lead time first, so "Tier 2"
// in a message is the tier that fires second and the row check below judges
// the same order the server does.
const inFiringOrder = (tiers: DraftTier[]): DraftTier[] =>
  [...tiers].sort((a, b) => b.leadTimeMinutes - a.leadTimeMinutes);

const toDraft = (schedule: SurgeSchedule): SurgeDraft => ({
  enabled: schedule.enabled,
  tiers: inFiringOrder(
    schedule.tiers.map((tier) => ({
      leadTimeMinutes: tier.lead_time_minutes,
      rate: centsToDollars(tier.rate_cents),
    })),
  ),
});

/**
 * Per-tier problems, indexed like the tiers, in the design's words (Figma
 * 40551:180301). The cap is checked per tier — tiers replace one another, so
 * their sum is never what a worker is paid.
 */
export const tierErrors = (
  draft: SurgeDraft,
  { maxCents }: Pick<Limits, 'maxCents'>,
): (string | null)[] => {
  const errors = draft.tiers.map((tier, index) => {
    const n = index + 1;
    if (!tier.rate.trim()) return `Tier ${n} needs a surge rate.`;
    const cents = dollarsToCents(tier.rate);
    if (cents == null || cents <= 0)
      return `Enter a dollar amount like 1.50 for tier ${n}.`;
    if (cents > maxCents)
      return `Tier ${n} exceeds the ${formatCents(maxCents)} cap. Reduce the rate to continue.`;
    return null;
  });

  // GRAVY-10860. Rows are held in firing order (longest lead time first), so
  // each amount has to beat the one above it. A schedule that falls saves
  // happily but cannot mean what it shows: the sweep only ever raises pay, so
  // the lower tier is stamped and changes nothing. The server refuses this too;
  // catching it here is what keeps Save from looking like it worked.
  //
  // Reported on the offending row, and only where that row has nothing else to
  // say, so it never competes with a blank or over-cap message on the same row.
  const cents = draft.tiers.map((tier) => dollarsToCents(tier.rate) ?? 0);
  const ordering = cents.map((amount, index) =>
    index > 0 && amount <= cents[index - 1]
      ? `Tier ${index + 1} must pay more than tier ${index}. Surge pay rises as the shift gets closer.`
      : null,
  );

  return errors.map((error, index) => error ?? ordering[index]);
};

const useSurgeDraft = (initial: SurgeSchedule, limits: Limits) => {
  const [draft, setDraft] = useState<SurgeDraft>(() => toDraft(initial));
  const [baseline, setBaseline] = useState<SurgeDraft>(() => toDraft(initial));

  // Replaces both the draft and what "unchanged" means — used when the saved
  // schedule arrives after the form has already mounted.
  const reset = useCallback((schedule: SurgeSchedule) => {
    setDraft(toDraft(schedule));
    setBaseline(toDraft(schedule));
  }, []);

  const unusedLeadTime = (tiers: DraftTier[]) =>
    limits.leadTimeOptions.find(
      (m) => !tiers.some((tier) => tier.leadTimeMinutes === m),
    );

  const setEnabled = (enabled: boolean) =>
    setDraft((d) => {
      // Turning surge on with nothing configured starts the admin at one tier:
      // a schedule that is on always has at least one (Q3).
      const first = unusedLeadTime([]);
      const tiers =
        enabled && d.tiers.length === 0 && first != null
          ? [{ leadTimeMinutes: first, rate: '' }]
          : d.tiers;
      return { enabled, tiers };
    });

  const updateTier = (index: number, patch: Partial<DraftTier>) =>
    setDraft((d) => ({
      ...d,
      tiers: inFiringOrder(
        d.tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)),
      ),
    }));

  // New tiers take the longest lead time not yet used, then take their place
  // in firing order: the first tier may already sit at the shortest option.
  const addTier = () =>
    setDraft((d) => {
      const free = [...limits.leadTimeOptions]
        .reverse()
        .find((m) => !d.tiers.some((tier) => tier.leadTimeMinutes === m));
      return free == null
        ? d
        : {
            ...d,
            tiers: inFiringOrder([
              ...d.tiers,
              { leadTimeMinutes: free, rate: '' },
            ]),
          };
    });

  // The last tier cannot be removed while surge is on (Q3); the editor hides
  // its trash, and this refuses it regardless.
  const removeTier = (index: number) =>
    setDraft((d) =>
      d.tiers.length <= 1
        ? d
        : { ...d, tiers: d.tiers.filter((_, i) => i !== index) },
    );

  const errors = useMemo(
    () =>
      draft.enabled ? tierErrors(draft, limits) : draft.tiers.map(() => null),
    [draft, limits],
  );

  const schedule = useMemo<SurgeSchedule>(
    () => ({
      enabled: draft.enabled,
      tiers: draft.tiers
        .map((tier) => ({
          lead_time_minutes: tier.leadTimeMinutes,
          rate_cents: dollarsToCents(tier.rate) ?? 0,
        }))
        .filter((tier: SurgeTier) => draft.enabled || tier.rate_cents > 0)
        .sort((a, b) => b.lead_time_minutes - a.lead_time_minutes),
    }),
    [draft],
  );

  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);
  const valid = errors.every((e) => e == null);

  return {
    draft,
    schedule,
    errors,
    dirty,
    valid,
    reset,
    setEnabled,
    updateTier,
    addTier,
    removeTier,
    canAddTier: draft.tiers.length < limits.leadTimeOptions.length,
  };
};

export type SurgeDraftState = ReturnType<typeof useSurgeDraft>;

export default useSurgeDraft;
